// External Packages
import { useState, useContext, useEffect } from "react";
import { FaUserCircle } from "react-icons/fa";
import { BiSolidMegaphone } from "react-icons/bi";
import { DataContext } from "../../context/data-context";
import { MdAdd, MdRefresh } from "react-icons/md";
import { useLocation } from "react-router-dom";

// Internal Components
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import SearchBar from "../UI/SearchBar";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import PaginationMenu from "../UI/PaginationMenu";
import ExportCsvButton from "../UI/ExportCsvButton";
import FilterMenu from "../UI/FilterMenu";
import DateFilter from "../UI/DateFilter";
import { formatCurrency, toTitleCase } from "../../util/format-text";
import { isoDateToString } from "../../util/format-date";
import DisputeInfo from "./DisputeInfo";

/** ========================================================================
 * * Disputes Page
 * Renders a page, showing a list of disputes on stripe.
 * ========================================================================== */

export default function Disputes() {
  const { navigate } = useContext(UtilityContext);
  const { locations } = useContext(DataContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    query: "",
    status: "all",
    limit: 100,
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [disputes, setDisputes] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState();

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "dispute",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/disputes?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      // Set Data
      setDisputes(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Filter Options
  const filterOptions = [
    {
      label: "Status",
      options: [
        { label: "All", value: "all" },
        { label: "Won", value: "won" },
        { label: "Lost", value: "lost" },
        { label: "Needs Response", value: "needs_response" },
        { label: "Under Review", value: "under_review" },
        { label: "Prevented", value: "prevented" },
        { label: "Warning - Needs Response", value: "warning_needs_response" },
        { label: "Warning - Under Review", value: "warning_under_review" },
        { label: "Warning - Closed", value: "warning_closed" },
      ],
      selected: filter.status,
    },
  ];

  // Calculate Total
  let total = 0;
  disputes.forEach((dispute) => {
    total += Number(dispute.amount);
  });

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container grid">
      <DisputeInfo
        open={!!selected}
        setOpen={setSelected}
        disputeId={selected}
      />
      <Heading title="Disputes" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          options={filterOptions}
          onSubmit={(f) => setFilter({ ...filter, ...f })}
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={disputes} filename="disputes" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : disputes.length > 0 ? (
        <div className="list">
          {disputes.map((dispute, idx) => (
            <div
              key={idx}
              onClick={() => setSelected(dispute.id)}
              className={`grid items-center grid-cols-2 text-sm pill lg:grid-cols-6 text-stone-200 ${
                dispute.status === "won"
                  ? "border-green-600"
                  : dispute.status === "lost"
                  ? "border-red-800"
                  : "border-yellow-600"
              } `}
            >
              <p className="text-stone-400">
                {isoDateToString(dispute.dateCreated)}
              </p>
              <p className="lg:col-span-2">
                {dispute.customer?.firstName} {dispute.customer?.lastName}
              </p>
              <p className="text-stone-400">
                {toTitleCase(dispute.reason?.replaceAll("_", " "))}
              </p>
              <p className="font-semibold">
                {toTitleCase(dispute.status.replaceAll("_", " "))}
              </p>
              <p className="ml-auto font-semibold">
                {formatCurrency(dispute.amount)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(limit) => setFilter({ ...filter, limit })}
        count={disputes.length}
        loading={loading}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(total)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
